import React, { useMemo } from 'react';
import { Box, Slider, Button, TextField, Typography } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import {
    Area, AreaChart, CartesianGrid, Legend, Line, ReferenceLine,
    ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis
} from 'recharts';
import { ReviewPolicy, WorkloadSimulatorResponse } from '../../services/benchmarkService';
import { MetricInfo, NotComputed, PanelError, PanelSkeleton, SectionCard } from './BenchmarkPrimitives';
import { useChartTheme } from '../visualizations/types';
import { outcomeAt } from './benchmarkModel';
import {
    DEFINITIONS, MONTHLY_HOURS_PER_FTE, count, fteFromAlerts, hasValue, percent, tierSetLabel
} from './BenchmarkVocabulary';
import { dsVar, ink } from '../../theme/tokens';
import { useThemeTokens } from '../../theme/ThemeModeProvider';

/**
 * Default average handling time per alert, in minutes.
 *
 * Shown on screen and editable, because it is an assumption about THIS
 * institution rather than anything the backtest measured. The previous version
 * hardcoded 45 minutes silently and printed "~4.5 FTE" as fixed text that did
 * not move with the slider or the data.
 *
 * Exported because the tab owns the value and seeds it from here.
 */
export const DEFAULT_MINUTES_PER_ALERT = 45;

interface TriageSimulatorProps {
    simulator: WorkloadSimulatorResponse | null;
    /**
     * The selected review level, owned by the tab.
     *
     * Controlled rather than local so the savings and coverage figures above
     * describe the same level the slider is set to — they used to be computed
     * from a fixed policy in the API and could disagree with it.
     */
    reviewPercent: number;
    onReviewPercentChange: (value: number) => void;
    /** The institution's average handling time, also owned by the tab. */
    minutesPerAlert: number;
    onMinutesPerAlertChange: (value: number) => void;
    /**
     * Too few cases for the run's tier cut-offs or a staffing estimate to mean
     * anything: presets and the FTE estimate are hidden.
     */
    indicative?: boolean;
    loading?: boolean;
    error?: string | null;
    onRetry?: () => void;
}

/**
 * "How much of the queue do you review?"
 *
 * One control, and the consequences of moving it. Every figure comes from the
 * run: when the run recorded no curve, the panel says so rather than falling
 * back to the invented 45% / 94.3% / 4.5 FTE it used to show.
 */
const TriageSimulator: React.FC<TriageSimulatorProps> = ({
    simulator, reviewPercent, onReviewPercentChange,
    minutesPerAlert, onMinutesPerAlertChange, indicative = false, loading = false, error, onRetry
}) => {
    const policies = simulator?.reviewPolicies ?? [];
    const chartTheme = useChartTheme();
    const tokens = useThemeTokens();

    const reading = useMemo(
        () => outcomeAt(simulator, reviewPercent / 100),
        [simulator, reviewPercent]
    );

    const chartData = useMemo(() => {
        if (!simulator) return [];
        return simulator.reductionCurve
            .slice()
            .sort((a, b) => a.reviewFraction - b.reviewFraction)
            .map((p) => ({
                reviewPercent: Number((p.reviewFraction * 100).toFixed(1)),
                confirmed: hasValue(p.truePositiveRecall) ? Number((p.truePositiveRecall * 100).toFixed(1)) : null,
                sars: hasValue(p.sarRecall) ? Number((p.sarRecall * 100).toFixed(1)) : null
            }));
    }, [simulator]);

    if (error) {
        return (
            <SectionCard title="How much of the queue do you review?" icon={<TuneIcon fontSize="small" />}>
                <PanelError message={error} onRetry={onRetry} />
            </SectionCard>
        );
    }
    if (loading) return <PanelSkeleton height={460} />;
    if (!simulator) return null;

    const reviewFraction = reviewPercent / 100;
    const alertsReviewed = Math.round(simulator.totalAlertCount * reviewFraction);
    const alertsAvoided = simulator.totalAlertCount - alertsReviewed;
    const fte = fteFromAlerts(alertsAvoided, minutesPerAlert);

    const hasAnyCurve = chartData.some((p) => p.confirmed !== null || p.sars !== null);

    // Green above nine in ten, amber in the eighties, red below — the point at
    // which giving up this much confirmed risk stops being an efficiency
    // decision and becomes a coverage one.
    const retentionColor = !hasValue(reading?.truePositiveRecall)
        ? '#64748B'
        : (reading!.truePositiveRecall as number) >= 0.9
            ? '#10B981'
            : (reading!.truePositiveRecall as number) >= 0.8
                ? '#FBBF24'
                : '#EF4444';

    const applyPolicy = (policy: ReviewPolicy) => {
        if (hasValue(policy.reviewFraction)) {
            onReviewPercentChange(Number((policy.reviewFraction * 100).toFixed(1)));
        }
    };

    return (
        <SectionCard
            title="How much of the queue do you review?"
            subtitle="Drag to set a cut-off. Everything below it would be closed without an investigator opening it."
            icon={<TuneIcon fontSize="small" />}
            accent="#38BDF8"
        >
            {/*
              Presets are the run's own tier cut-offs, not named house policies.
              The previous two buttons ("Critical Rush", "Balanced Operations")
              were hardcoded to 26.5% and 55.2% regardless of the data.
            */}
            {indicative && (
                <Typography sx={{ fontSize: '0.75rem', color: ink('#FBBF24'), mb: 2, lineHeight: 1.6 }}>
                    Review presets and staffing estimates are hidden: this run scored too few cases for either to hold.
                </Typography>
            )}

            {policies.length > 0 && !indicative && (
                <Box className="flex flex-wrap gap-2 mb-5">
                    {policies.map((policy) => (
                        <Button
                            key={policy.activeTiers.join('-')}
                            onClick={() => applyPolicy(policy)}
                            size="small"
                            sx={{
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: dsVar('fg-soft'),
                                border: `1px solid ${dsVar('line')}`,
                                borderRadius: '8px',
                                px: 1.5,
                                '&:hover': { borderColor: dsVar('accent-fg'), backgroundColor: 'rgba(255, 222, 89, 0.08)' }
                            }}
                        >
                            {tierSetLabel(policy.activeTiers)} — {percent(policy.reviewFraction, 0)} of queue
                        </Button>
                    ))}
                </Box>
            )}

            <Box className="flex items-baseline gap-2 mb-1">
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}>Review the top</Typography>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: dsVar('accent-fg') }}>
                    {reviewPercent.toFixed(1)}%
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}>
                    of the queue — {count(alertsReviewed)} alerts
                </Typography>
            </Box>

            <Slider
                value={reviewPercent}
                min={5}
                max={100}
                step={0.5}
                onChange={(_, value) => onReviewPercentChange(value as number)}
                aria-label="Share of the alert queue sent for review"
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v}%`}
                sx={{
                    color: dsVar('accent-fg'),
                    '& .MuiSlider-thumb': { backgroundColor: dsVar('accent') },
                    '& .MuiSlider-rail': { backgroundColor: dsVar('line'), opacity: 1 }
                }}
            />

            <Box className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                <Box className="rounded-xl bg-page/60 border border-line p-4">
                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>Alerts not reviewed</Typography>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: ink('#38BDF8'), mt: 0.5 }}>
                        {percent(1 - reviewFraction)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted') }}>
                        {count(alertsAvoided)} alerts
                    </Typography>
                </Box>

                <Box className="rounded-xl bg-page/60 border border-line p-4">
                    <Box className="flex items-center gap-1.5">
                        <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>Confirmed risks still caught</Typography>
                        <MetricInfo definition={DEFINITIONS.riskRetained} />
                    </Box>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: ink(retentionColor), mt: 0.5 }}>
                        {percent(reading?.truePositiveRecall)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted') }}>
                        {hasValue(reading?.truePositiveRecall)
                            ? `${count(Math.round((reading!.truePositiveRecall as number) * simulator.totalTruePositives))} of ${count(simulator.totalTruePositives)}`
                            : 'No curve recorded for this run'}
                    </Typography>
                </Box>

                <Box className="rounded-xl bg-page/60 border border-line p-4">
                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>SARs still caught</Typography>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: ink('#FBBF24'), mt: 0.5 }}>
                        {percent(reading?.sarRecall)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted') }}>
                        {hasValue(reading?.sarRecall)
                            ? `${count(Math.round((reading!.sarRecall as number) * simulator.totalSARsFiled))} of ${count(simulator.totalSARsFiled)}`
                            : 'No curve recorded for this run'}
                    </Typography>
                </Box>
            </Box>

            {/*
              The time saving is an estimate built on an assumption, so the
              assumption is on screen and adjustable rather than baked into a
              constant the reader cannot see.
            */}
            {!indicative && (
            <Box className="mt-4 rounded-xl bg-page/40 border border-line p-4 flex flex-wrap items-center gap-x-4 gap-y-3">
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft') }}>
                    At an average of
                </Typography>
                <TextField
                    value={minutesPerAlert}
                    onChange={(e) => {
                        const next = parseInt(e.target.value, 10);
                        onMinutesPerAlertChange(Number.isFinite(next) && next > 0 ? next : 0);
                    }}
                    size="small"
                    type="number"
                    inputProps={{ min: 1, max: 480, 'aria-label': 'Average minutes to work one alert' }}
                    sx={{
                        width: 90,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: dsVar('surface'),
                            color: dsVar('fg-body'),
                            fontSize: '0.875rem',
                            '& fieldset': { borderColor: dsVar('line') },
                            '&:hover fieldset': { borderColor: dsVar('line') },
                            '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') }
                        }
                    }}
                />
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft') }}>
                    minutes to work one alert, this frees about
                </Typography>
                <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: dsVar('accent-fg') }}>
                    {fte === null ? '—' : `${fte.toFixed(1)} full-time investigators`}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), width: '100%' }}>
                    Based on a {MONTHLY_HOURS_PER_FTE}-hour month and this run's caseload. Adjust the handling time to match your own.
                </Typography>
            </Box>
            )}

            <Box sx={{ mt: 3 }}>
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: dsVar('fg-soft'), mb: 1.5 }}>
                    What you keep at every cut-off
                </Typography>
                {hasAnyCurve ? (
                    <Box sx={{ width: '100%', height: 260 }}>
                        <ResponsiveContainer>
                            <AreaChart data={chartData} margin={{ top: 8, right: 12, bottom: 4, left: -12 }}>
                                <defs>
                                    <linearGradient id="confirmedFill" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={tokens.success} stopOpacity={0.35} />
                                        <stop offset="100%" stopColor={tokens.success} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.border.default} vertical={false} />
                                <XAxis
                                    dataKey="reviewPercent"
                                    stroke={chartTheme.text.muted}
                                    fontSize={11}
                                    tickFormatter={(v) => `${v}%`}
                                    label={{ value: 'Share of queue reviewed', position: 'insideBottom', offset: -2, fill: tokens['fg-muted'], fontSize: 11 }}
                                />
                                <YAxis
                                    stroke={chartTheme.text.muted}
                                    fontSize={11}
                                    domain={[0, 100]}
                                    tickFormatter={(v) => `${v}%`}
                                />
                                <RechartsTooltip
                                    contentStyle={{
                                        backgroundColor: dsVar('surface'),
                                        border: `1px solid ${dsVar('line')}`,
                                        borderRadius: 8,
                                        color: dsVar('fg'),
                                        fontSize: 12
                                    }}
                                    formatter={(value: any, name: string) => [value === null ? '—' : `${value}%`, name]}
                                    labelFormatter={(label) => `Reviewing top ${label}%`}
                                />
                                <Legend wrapperStyle={{ fontSize: 11, color: dsVar('fg-2') }} />
                                <Area
                                    type="monotone"
                                    dataKey="confirmed"
                                    name="Confirmed risks caught"
                                    stroke={tokens.success}
                                    strokeWidth={2}
                                    fill="url(#confirmedFill)"
                                    connectNulls
                                />
                                <Line
                                    type="monotone"
                                    dataKey="sars"
                                    name="SARs caught"
                                    stroke={tokens.warning}
                                    strokeWidth={2}
                                    dot={false}
                                    connectNulls
                                />
                                <ReferenceLine
                                    x={reviewPercent}
                                    stroke={tokens['accent-fg']}
                                    strokeDasharray="4 4"
                                    label={{ value: 'Your cut-off', fill: tokens['accent-fg'], fontSize: 10, position: 'top' }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Box>
                ) : (
                    <NotComputed detail="This run recorded no reduction curve, so the trade-off cannot be plotted." />
                )}
            </Box>
        </SectionCard>
    );
};

export default TriageSimulator;
